import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import _ from 'lodash';
import moment from 'moment';

import { getProductsByCampaignType } from 'services/api/products';
import {
  requestInvitationsAndTeam,
  openInviteModal as openInviteModalAction,
  closeInviteModal as closeInviteModalAction,
} from 'services/actions/socialEcommActions';

import withViewSize from 'HOC/withViewSize/withViewSize';
import {
  btnLocationEnum,
  REACTIVATE_TRACKING_DATA,
  EXPERIENCES,
  trackUpgradeStarted,
  trackReactivateStarted,
} from 'utils/segment';
import {
  REDIRECT_ACTION,
  getUserType,
  trackInviteStarted,
  sortPreviewProducts,
} from 'utils/social-ecomm';
import { SUBSCRIPTION_STATUS } from 'utils/user';
import { selectPageTrackingData } from 'selectors/trackingSelector';
import ShareSale from 'shared-components/ShareSale/ShareSale';
import PreviewProducts from 'shared-components/PreviewProducts/PreviewProducts';
import InviteModal from 'shared-components/InviteModal/InviteModal';
import {
  showSuccessToast,
  showErrorToast,
} from 'shared-components/BaseToast/Toast';
import PastDue from 'shared-components/PastDue/PastDue';
import { CAMPAIGN_TYPE } from 'utils/campaign';
import useFacebookScript from 'hooks/useFacebookScript';
import ClosedSocialBanner from './ClosedSocialBanner/ClosedSocialBanner';
import ClosedSocialLayout from './ClosedSocialLayout/ClosedSocialLayout';
import FriendsList from './FriendsList/FriendsList';
import HowTheSaleWorks from './HowTheSaleWorks/HowTheSaleWorks';
import Separator from './Separator/Separator';

const INVITE_STATE = {
  invite: 'redeemed-sale',
  upgrade: 'seasonal',
  reactivate: 'expired',
};

const getFriendsList = (list, userId) =>
  _.reject(list, (item) => item.userId === userId);

const getCTAType = (subscriptionState, isAccountPastDue, userSocialAccess) => {
  if (subscriptionState === SUBSCRIPTION_STATUS.expired) return 'reactivate';
  if (isAccountPastDue) return 'checkMyAccount';
  return userSocialAccess ? 'invite' : 'upgrade';
};

const CTAs = {
  sendInvite: 'Send Invite',
  upgrade: 'Upgrade Now',
  reactivate: 'Reactivate Now',
};

const ClosedSocialPage = ({
  planCode,
  premiumStartDate,
  startDate,
  premiumEndDate,
  endDate,
  socialWindow,
  userSocialAccess,
  userId,
  subscriptionState,
  isAccountPastDue,
  subscription,
  redeemedMembers,
  loading,
  viewSize,
  fetchInvitationsAndTeam,
  openInviteModal,
  closeInviteModal,
  trackingData,
}) => {
  const [previewProducts, setPreviewProducts] = useState([]);
  useFacebookScript();

  const handleInviteSent = (emailSent) => {
    closeInviteModal();
    if (emailSent)
      showSuccessToast(
        'Your Invite Has Been Sent',
        'You’ve successfully invited your friend to shop with you!'
      );
  };
  const handleInviteError = (title) => showErrorToast(title);

  const handleClickInvite = () => {
    trackInviteStarted(trackingData, 'Pre-Sale Page', 'SEND INVITE');
    openInviteModal(btnLocationEnum.preSalePage);
  };

  useEffect(() => {
    const fetchPreviewProducts = async () => {
      const response = await getProductsByCampaignType(
        CAMPAIGN_TYPE.edit,
        true
      );
      const data = _.get(response, 'data.items[0]', {});
      const { productIds, products } = data;
      const previewProductsData = sortPreviewProducts(productIds, products);
      setPreviewProducts(previewProductsData);
    };
    fetchInvitationsAndTeam();
    fetchPreviewProducts();
  }, []);

  if (loading) return null;

  const userType = getUserType(planCode);
  const friendsList = getFriendsList(redeemedMembers, userId);
  const campaignStartDate = socialWindow === 1 ? premiumStartDate : startDate;
  const campaignEndDate = socialWindow === 1 ? premiumEndDate : endDate;
  const campaignEndDatePlusSeven = moment(campaignEndDate)
    .tz('America/Los_Angeles')
    .add(7, 'days')
    .format('MMMM Do');

  const handleClickUpgrade = () => {
    trackUpgradeStarted({
      cta: CTAs.upgrade,
      experience: EXPERIENCES.edit,
    }).finally(() => REDIRECT_ACTION.upgrade());
  };

  const handleClickReactivate = () => {
    const isExpired = subscriptionState === SUBSCRIPTION_STATUS.expired;
    const {
      expiredType,
      canceledType,
      expiredReactivationType,
      canceledReactivationType,
    } = REACTIVATE_TRACKING_DATA;
    const type = isExpired ? expiredType : canceledType;
    const reactivationType = isExpired
      ? expiredReactivationType
      : `${canceledReactivationType} ${subscription}`;
    trackReactivateStarted({
      cta: CTAs.reactivate,
      type,
      experience: EXPERIENCES.edit,
      reactivationType,
    }).finally(() => REDIRECT_ACTION.reactivate('edit'));
  };

  const cta = {
    invite: {
      buttonText: CTAs.sendInvite,
      buttonAction: handleClickInvite,
    },
    upgrade: {
      buttonText: CTAs.upgrade,
      buttonAction: handleClickUpgrade,
    },
    reactivate: {
      buttonText: CTAs.reactivate,
      buttonAction: handleClickReactivate,
    },
    checkMyAccount: {
      buttonText: 'Check My Account',
      buttonAction: REDIRECT_ACTION.checkMyAccount,
    },
  };
  const ctaType = getCTAType(
    subscriptionState,
    isAccountPastDue,
    userSocialAccess
  );
  const { buttonAction, buttonText } = _.get(cta, ctaType);
  const inviteState = _.get(INVITE_STATE, ctaType);
  const shareSaleCopy = {
    title: 'Share Our Members-Only Sale Experience',
    subtitle: `Let your friends get a no-pressure, risk-free taste of FabFitFun. It doesn't cost them anything to sign up! Plus Members get a <strong>$15 credit</strong> for each referred friend who becomes a member by ${campaignEndDatePlusSeven}.`,
    boxTitle: "They'll be able to",
    list: [
      'Shop and discover products with you.',
      'Save up to 70% off on premium products.',
      'Invite others to join the fun.',
      'Earn a <strong>$10 credit</strong> to save even more on premium products.',
    ],
    buttonText: 'Send Invite',
  };

  return (
    <>
      <ClosedSocialBanner
        campaignStartDate={campaignStartDate}
        copyType={ctaType}
        buttonAction={buttonAction}
        buttonText={buttonText}
      />
      <ClosedSocialLayout>
        {userSocialAccess && _.size(friendsList) > 0 && (
          <>
            <FriendsList friendsList={friendsList} />
            <Separator />
          </>
        )}
        <HowTheSaleWorks
          userType={userType}
          campaignEndDatePlusSeven={campaignEndDatePlusSeven}
        />
        <Separator />
        <PreviewProducts
          products={previewProducts}
          viewSize={viewSize}
          toggleInviteModal={handleClickInvite}
          inviteState={inviteState}
        />
        <Separator />
        <ShareSale
          title={shareSaleCopy.title}
          subtitle={shareSaleCopy.subtitle}
          boxTitle={shareSaleCopy.boxTitle}
          list={shareSaleCopy.list}
          buttonText={buttonText}
          viewSize={viewSize}
          onClickButton={buttonAction}
        />
      </ClosedSocialLayout>
      <InviteModal
        onInviteSent={handleInviteSent}
        onInviteError={handleInviteError}
      />
      <PastDue isAccountPastDue={isAccountPastDue} />
    </>
  );
};

ClosedSocialPage.propTypes = {
  planCode: PropTypes.string.isRequired,
  premiumStartDate: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  premiumEndDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  socialWindow: PropTypes.number.isRequired,
  userSocialAccess: PropTypes.bool.isRequired,
  userId: PropTypes.number.isRequired,
  subscriptionState: PropTypes.string.isRequired,
  isAccountPastDue: PropTypes.bool.isRequired,
  subscription: PropTypes.string.isRequired,
  redeemedMembers: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      invitedByName: PropTypes.string,
    })
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  trackingData: PropTypes.shape().isRequired,
  viewSize: PropTypes.string.isRequired,
  fetchInvitationsAndTeam: PropTypes.func.isRequired,
  openInviteModal: PropTypes.func.isRequired,
  closeInviteModal: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  premiumStartDate: state.campaignState.premiumStartDate,
  startDate: state.campaignState.startDate,
  premiumEndDate: state.campaignState.premiumEndDate,
  endDate: state.campaignState.endDate,
  socialWindow: state.campaignState.socialWindow,
  userSocialAccess: state.campaignState.userSocialAccess,
  userId: state.userState.userId,
  subscriptionState: state.userState.subscriptionState,
  isAccountPastDue: state.userState.isAccountPastDue,
  subscription: state.userState.subscription,
  redeemedMembers: state.socialEcommState.redeemedMembers,
  loading: state.socialEcommState.fetchingInvitationsAndTeam,
  viewSize: state.viewState.viewSize,
  trackingData: selectPageTrackingData(state),
});

const mapDispatchToProps = {
  fetchInvitationsAndTeam: requestInvitationsAndTeam,
  openInviteModal: openInviteModalAction,
  closeInviteModal: closeInviteModalAction,
};

export default compose(
  withViewSize,
  connect(mapStateToProps, mapDispatchToProps)
)(ClosedSocialPage);
